<template>
  <div class="SolutionsPage">
    <div class="solutions-banner">
      <div class="box">
        <div class="banner-content center">
          <h1>{{ banner.title }}</h1>
          <p>{{ banner.subtitle }}</p>
          <div class="banner-features">
            <span v-for="(feature, index) in parsedBannerFeatures" :key="index">{{ feature.normal_text }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="solutions-content Pt75">
      <div class="box">
        <div class="PubTitle center">
          <h2>我们的解决方案</h2>
          <p>深入行业特定需求，从电子车间到仓储物流，我们都有成熟的案例与方案</p>
        </div>

        <div class="solutions-tabs-container">
          <ul class="solutions-tabs">
            <li
                v-for="category in solutionCategories"
                :key="category.id"
                :class="{ active: activeSolutionId === category.id }"
                @click="selectSolution(category.id)"
            >
              {{ category.name }}
            </li>
          </ul>
        </div>

        <div class="scenarios-grid">
          <div
              v-for="(scenario, index) in scenarios"
              :key="index"
              class="scenario-card"
          >
            <div class="card-image">
              <img :src="scenario.image_img" :alt="scenario.title"/>
              <div class="image-overlay">
                <router-link :to="'/solutions/' + scenario.id" class="details-link">查看详情 →</router-link>
              </div>
            </div>
            <div class="card-content">
              <h3>{{ scenario.title }}</h3>
              <p class="scenario-description">{{ scenario.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPageHeader, getCaseCategory, getCaseScenario } from '@/api/request.js';

export default {
  name: 'SolutionsPage',
  data() {
    return {
      banner: {},
      activeSolutionId: null,
      solutionCategories: [],
      scenarios: [],
      VUE_APP_IMAGE_URL: process.env.VUE_APP_IMAGE_URL,
    };
  },
  computed: {
    parsedBannerFeatures() {
      try {
        return this.banner.features ? JSON.parse(this.banner.features) : [];
      } catch (error) {
        console.error('Error parsing banner features:', error);
        return [];
      }
    },
  },
  methods: {
    async fetchSolutionData(initialCategoryId = null) {
      try {
        const categoryResponse = await getCaseCategory();
        this.solutionCategories = categoryResponse.list;
        
        let categoryIdToLoad = initialCategoryId;
        if (!categoryIdToLoad && this.solutionCategories.length > 0) {
          categoryIdToLoad = this.solutionCategories[0].id;
        }
        
        if (categoryIdToLoad) {
          this.selectSolution(categoryIdToLoad);
        }
      } catch (error) {
        console.error('Error fetching solution categories:', error);
      }
    },
    async fetchScenarios(categoryId) {
      try {
        const scenarioResponse = await getCaseScenario({ category_id: categoryId });
        this.scenarios = scenarioResponse.list;
      } catch (error) {
        console.error(`Error fetching scenarios for category ${categoryId}:`, error);
      }
    },
    selectSolution(solutionId) {
      this.activeSolutionId = solutionId;
      this.fetchScenarios(solutionId);
    },
    async fetchData() {
      try {
        const bannerResponse = await getPageHeader({ banner_key: '解决方案' });
        if (bannerResponse.list.length > 0) {
          this.banner = bannerResponse.list[0];
        } else {
          this.banner = {};
        }
      } catch (error) {
        console.error('Error fetching page header:', error);
      }
    },
  },
  watch: {
    '$route.query.category': {
      handler(newVal) {
        if (newVal && this.solutionCategories.length > 0) {
          const categoryId = parseInt(newVal, 10);
          if (this.solutionCategories.some(cat => cat.id === categoryId)) {
            this.selectSolution(categoryId);
          }
        }
      },
      immediate: false, // We handle initial load in created
    },
  },
  created() {
    this.fetchData();
    const initialCategoryId = this.$route.query.category ? parseInt(this.$route.query.category, 10) : null;
    this.fetchSolutionData(initialCategoryId);
  },
};
</script>

<style scoped>
/* Inherit General Styles for Consistency */
.Pt75 {
  padding-top: 75px;
  padding-bottom: 75px;
}
.box {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}
.center {
  text-align: center;
}
.PubTitle h2 {
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}
.PubTitle p {
  font-size: 18px;
  color: #666;
  max-width: 800px;
  margin: 0 auto 50px;
  line-height: 1.7;
}

/* Solutions Page Banner */
.solutions-banner {
  background: #47975a;
  color: white;
  padding: 100px 0;
  position: relative;
  overflow: hidden;
}
.banner-content h1 {
  font-size: 56px;
  font-weight: bold;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}
.banner-content > p {
  font-size: 24px;
  opacity: 0.95;
  margin-bottom: 40px;
}
.banner-features {
  display: flex;
  justify-content: center;
  gap: 30px;
}
.banner-features span {
  background: rgba(255, 255, 255, 0.2);
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Tab Navigation Styles */
.solutions-tabs-container {
  display: flex;
  justify-content: center;
  margin-bottom: 60px;
}
.solutions-tabs {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  background-color: #f0f2f5;
  border-radius: 50px;
  padding: 8px;
}
.solutions-tabs li {
  padding: 12px 30px;
  font-size: 18px;
  font-weight: 500;
  color: #555;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.solutions-tabs li:hover {
  background-color: #e2e8f0;
}
.solutions-tabs li.active {
  background-color: #47975a;
  color: #fff;
  box-shadow: 0 4px 15px rgba(71, 151, 90, 0.3);
}

/* Scenarios Grid Styles */
.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
}
.scenario-card {
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.scenario-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
}

/* Card Image Styles */
.card-image {
  position: relative;
  height: 240px;
  overflow: hidden;
}
.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.scenario-card:hover .card-image img {
  transform: scale(1.08);
}
.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.scenario-card:hover .image-overlay {
  opacity: 1;
}
.details-link {
  color: #fff;
  background-color: rgba(71, 151, 90, 0.8);
  padding: 12px 24px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.3s ease;
}
.details-link:hover {
  background-color: #47975a;
}

/* Card Content Styles */
.card-content {
  padding: 25px;
  flex-grow: 1;
}
.card-content h3 {
  font-size: 22px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
}
.scenario-description {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
}

/* Responsive Design */
@media (max-width: 768px) {
  .Pt75 {
    padding-top: 50px;
    padding-bottom: 50px;
  }
  .banner-content h1 {
    font-size: 36px;
  }
  .banner-content > p {
    font-size: 18px;
  }
  .PubTitle h2 {
    font-size: 28px;
  }
  .PubTitle p {
    font-size: 16px;
    margin-bottom: 40px;
  }
  .solutions-tabs li {
    padding: 10px 20px;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .banner-features {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }
  .solutions-tabs {
    gap: 8px;
    padding: 6px;
  }
  .solutions-tabs li {
    flex-grow: 1;
    text-align: center;
    padding: 10px 15px;
    font-size: 15px;
  }
  .scenarios-grid {
    grid-template-columns: 1fr;
  }
}
</style>